import { auth, db } from "./firebase";
import {
  addMockDoctorVisitRecord,
  archiveMockDoctorVisitRecord,
  getMockDoctorVisitsForChild,
  isE2EMockEnabled,
  linkMockDoctorVisitProvider,
  updateMockDoctorVisitRecord,
} from './e2eMock';
import {
  collection,
  addDoc,
  query,
  where,
  getDocs,
  getDoc,
  doc,
  updateDoc,
} from "firebase/firestore";
import {
  archiveDoctorVisitTimelineEntry,
  syncDoctorVisitTimelineEntry,
} from './doctorVisitTimelineService';

// Add a new doctor visit for a child
export const addDoctorVisit = async (visitData) => {
  try {
    if (isE2EMockEnabled()) {
      const visitId = `visit-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      addMockDoctorVisitRecord(visitId, visitData);
      return visitId;
    }

    const docRef = await addDoc(collection(db, "doctorVisits"), visitData);
    await syncDoctorVisitTimelineEntry(docRef.id, visitData);
    return docRef.id;
  } catch (e) {
    console.error("Error adding doctor visit: ", e);
    throw e;
  }
};

// Fetch all doctor visits for a specific child
export const fetchDoctorVisits = async (childId) => {
  try {
    if (isE2EMockEnabled()) {
      return getMockDoctorVisitsForChild(childId)
        .filter((visit) => visit.status !== 'deleted' && visit.status !== 'archived')
        .map((visit) => ({ ...visit }));
    }

    const q = query(
      collection(db, "doctorVisits"),
      where("childId", "==", childId)
    );
    const querySnapshot = await getDocs(q);
    const visits = [];
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.status === 'deleted' || data.status === 'archived') {
        return;
      }
      visits.push({ id: doc.id, ...data });
    });
    return visits;
  } catch (e) {
    console.error("Error fetching doctor visits: ", e);
    throw e;
  }
};

// Update an existing doctor visit
export const updateDoctorVisit = async (visitId, updatedData) => {
  try {
    if (isE2EMockEnabled()) {
      updateMockDoctorVisitRecord(visitId, updatedData);
      return;
    }

    const visitRef = doc(db, "doctorVisits", visitId);
    await updateDoc(visitRef, updatedData);
    await syncDoctorVisitTimelineEntry(visitId, updatedData);
  } catch (e) {
    console.error("Error updating doctor visit: ", e);
    throw e;
  }
};

export const linkDoctorVisitProvider = async (visitId, providerData = {}) => {
  try {
    if (isE2EMockEnabled()) {
      linkMockDoctorVisitProvider(visitId, providerData.providerId || null);
      return;
    }

    const visitRef = doc(db, "doctorVisits", visitId);
    const existingSnapshot = await getDoc(visitRef);
    const existingVisit = existingSnapshot.exists() ? existingSnapshot.data() : {};
    const mergedVisit = {
      ...existingVisit,
      providerId: providerData.providerId || null,
      updatedAt: new Date(),
    };

    await updateDoc(visitRef, {
      providerId: providerData.providerId || null,
      updatedAt: new Date(),
    });
    await syncDoctorVisitTimelineEntry(visitId, mergedVisit);
  } catch (e) {
    console.error("Error linking doctor visit provider: ", e);
    throw e;
  }
};

// Delete a doctor visit
export const deleteDoctorVisit = async (visitId) => {
  try {
    if (isE2EMockEnabled()) {
      archiveMockDoctorVisitRecord(visitId);
      return;
    }

    await updateDoc(doc(db, "doctorVisits", visitId), {
      status: 'deleted',
      deletedAt: new Date(),
      deletedBy: auth.currentUser?.uid || null,
      updatedAt: new Date(),
    });
    await archiveDoctorVisitTimelineEntry(visitId);
  } catch (e) {
    console.error("Error deleting doctor visit: ", e);
    throw e;
  }
};
